import React, { useEffect, useState } from "react";
import {
  $Menu,
  $Drawer,
  $Empty,
  $Skeleton,
  $Button,
  $Tooltip,
} from "us.common/components";
import { DownOutlined, PlusOutlined } from "us.icons";
import { AddCategory, MoreOption } from "./Components";
import { RootState } from "us.helper/types";
import { connect, ConnectedProps } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { getUrlParams } from "components/EditorHome/Functions";
import { URLParams } from "components/EditorHome/Constants";
import { useTranslation } from "react-i18next";
import * as Actions from "actions";
import { Group } from "./Interfaces";
import { checkCategoryIdInGroupId } from "./Functions";

/**
 * @description - Template editor home menu component.
 * @link Design Document -
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 11/09/2022
 */
const TemplateMenu: React.FC<PropsFromRedux> = (props) => {
  const { templates, addCategoryDrawer, openDrawer } = props;
  const { t } = useTranslation();

  const { search } = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(search);

  const { categoryId, groupId, filtered } = getUrlParams(search);
  const [openKeys, setOpenKeys] = useState<Array<string>>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group>({
    name: "",
    id: 0,
  });

  useEffect(() => {
    if (templates.groups && !templates.loading) {
      if (
        filtered &&
        templates.groups.length > 0 &&
        !checkCategoryIdInGroupId(templates.groups, groupId, categoryId)
      ) {
        const keys = templates.groups.map((group) => group.groupId);
        setOpenKeys(keys);
        updateUrl(
          templates.groups[0].groupId,
          templates.groups[0].categories[0].categoryId
        );
      } else {
        groupId && setOpenKeys([groupId]);
      }
    }
  }, [templates.groups, filtered]);

  /**
   * @description - Update the browser URL according to the result
   * @param {string} groupId - Template group id
   * @param {string} categoryId - Template category id
   */
  const updateUrl = (groupId: string, categoryId: string) => {
    urlParams.set(URLParams.GROUP_ID, groupId);
    urlParams.set(URLParams.CATEGORY_ID, categoryId);
    navigate({ search: urlParams.toString() }, { replace: true });
  };

  /**
   * @description - Close drawer
   */
  const onClose = () => {
    openDrawer && openDrawer({ title: "", visibility: false });
  };

  return (
    <>
      <div className="editor-home-layout-side">
        <$Skeleton
          loading={templates.loading}
          active
          paragraph={{
            rows: 3,
          }}
          className={templates.loading ? "p-3" : ""}
        >
          <$Menu
            mode="inline"
            // selectedKeys={categoryId ? [categoryId] : []}    //categoryId is duplicated across different groups
            selectedKeys={categoryId && groupId ? [`${groupId}-${categoryId}`] : []}
            openKeys={openKeys}
            onOpenChange={(openKeys: any) => setOpenKeys(openKeys)}
            onClick={(menuItem) =>
            {
              // updateUrl(menuItem?.keyPath[1], menuItem?.key)                       //commented due to to categoryId issue
              const [groupIdFromKey, categoryIdFromKey] = menuItem.key.split("-");    //extracting groupId and categoryId from newly introduced key `${groupId}-${categoryId}` 
              updateUrl(groupIdFromKey, categoryIdFromKey);
            }
            }
            className="cat-menu"
          >
            {templates.groups?.map((group: any) => {
              const { groupId, groupName } = group;
              return (
                <React.Fragment key={`group-${groupId}`}>
                  <$Tooltip
                    placement="top"
                    title={t(
                      "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADD_NEW_APPLICATION"
                    )}
                  >
                    <$Button
                      size="small"
                      onClick={() => {
                        openDrawer &&
                          openDrawer({
                            title: t(
                              "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADD_NEW_APPLICATION"
                            ),
                            visibility: true,
                          });
                        setSelectedGroup({ name: groupName, id: groupId });
                      }}
                      className="cat-add-btn"
                    >
                      <PlusOutlined />
                    </$Button>
                  </$Tooltip>
                  <$Menu.SubMenu
                    key={`${group?.groupId}`}
                    title={
                      <div className="ant-menu-title-content-inner">
                        <DownOutlined />
                        <div className="cat-title">{group?.groupName}</div>
                      </div>
                    }
                  >
                    {group.categories?.map((category: any) => {
                      const { categoryId, displayName } = category ?? {};
                      return (
                        // <$Menu.Item key={categoryId}>                //categoryId is duplicated across different groups
                        <$Menu.Item key={`${groupId}-${categoryId}`}>   
                          <div className="cm-title">{displayName}</div>
                          <MoreOption
                            category={category}
                            categories={group.categories?.map((category: any) =>
                              category.displayName.toLowerCase()
                            )}
                          />
                        </$Menu.Item>
                      );
                    })}
                  </$Menu.SubMenu>
                </React.Fragment>
              );
            })}
          </$Menu>
        </$Skeleton>
        {templates.groups?.length == 0 && !templates.loading && (
          <$Empty image={$Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>
      <$Drawer
        title={addCategoryDrawer.title}
        placement={"right"}
        width={"450px"}
        onClose={() =>
          openDrawer && openDrawer({ ...addCategoryDrawer, visibility: false })
        }
        open={addCategoryDrawer.visibility}
        destroyOnClose={true}
      >
        <AddCategory group={selectedGroup} onClose={() => onClose()} />
      </$Drawer>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templatesHistory, editorHome } = state;
  const { templates } = templatesHistory;
  const { addCategoryDrawer } = editorHome;
  return {
    templates,
    addCategoryDrawer,
  };
};

const { editorHome } = Actions;
const { templateRules, templateCategory } = editorHome;

const mapDispatchToProps = {
  getAllTemplateRules: templateRules.get,
  openDrawer: templateCategory.openDrawer,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(TemplateMenu);
